import { useCallback, useEffect,useMemo, useState } from "react";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import { AutocompleteTags } from "@components_v2/autocomplete";
import { useTranslation } from "react-i18next";
import i18n from "@lib/i18n";

const TagsInput = ({ tags, disabled = false, onDeleteTag, onAddTag, isEdit, maxCount, isClear }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState("");
    const [validationError, setValidationError] = useState(null);
    const MAX_COUNT = maxCount || 100;

    const validate = useCallback(
        (newValue, currentTags = tags) => {
            if (!newValue) return null;
            if (currentTags?.length >= MAX_COUNT)
                return {
                    level: "error",
                    message: i18n.t(`common::msg::The maximum number of tags, {{maxCount}}, has been exceeded.`, {
                        maxCount: MAX_COUNT,
                    }),
                };
            return validateInput(newValue, {
                maxLength: LIMIT_CHAR.tag,
                uniqueIn: [newValue].concat(currentTags?.map((tag) => tag.value) || []),
            });
        },
        [tags, MAX_COUNT],
    );

    const onHandleDeleteTag = useCallback(
        (index) => {
            const updatedTags = tags.filter((_, i) => i !== index);
            setValidationError(validate(value, updatedTags));
            onDeleteTag(index);
        },
        [onDeleteTag, tags, value, validate],
    );

    const handleSetValue = useCallback(
        (newValue) => {
            setValue(newValue);
            setValidationError(validate(newValue));
        },
        [validate],
    );

    const onHandleSubmitTag = useCallback(() => {
        if (!value || validationError) return;
        onAddTag({ value });
        setValue("");
        setValidationError(null);
    }, [value, onAddTag, validationError]);

    useEffect(() => {
        if (isClear) {
            setValue("");
            setValidationError(null);
        }
    }, [isClear]);

    return (
        <AutocompleteTags
            type={"tag"}
            value={value}
            placeholder={t(`common::msg::Enter after typing`)}
            tags={tags}
            disabled={disabled}
            setValue={handleSetValue}
            onDelete={onHandleDeleteTag}
            onSubmit={onHandleSubmitTag}
            isEdit={isEdit}
            maxCount={MAX_COUNT}
            helperText={validationError?.message || ""}
            error={validationError?.level === "error"}
        />
    );
};

export default TagsInput;
